import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../utils/api';
import {
  Typography,
  Container,
  Paper,
  TextField,
  Button,
  Grid,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import ja from 'date-fns/locale/ja';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { format, addDays, parseISO, isValid } from 'date-fns';
import './Shifts.css';

function Shifts() {
  const navigate = useNavigate();
  const { currentUser, token, isLoggedIn, userRole } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isNextDay, setIsNextDay] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingShift, setEditingShift] = useState(null);

  // 投稿ユーザーかどうかチェック
  const isPosterUser = userRole === 'poster' || (currentUser && currentUser.role === 'poster');

  useEffect(() => {
    // 投稿ユーザーでない場合はホームにリダイレクト
    if (isLoggedIn && !isPosterUser) {
      navigate('/');
    }
    
    if (isLoggedIn && isPosterUser) {
      fetchShifts();
    }
  }, [isLoggedIn, isPosterUser, navigate]);

  // シフト一覧を取得
  const fetchShifts = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/api/shifts/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setShifts(response.data?.shifts || []);
    } catch (err) {
      
      setError('シフト情報の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  // シフト登録処理
  const handleRegisterShift = async () => {
    try {
      if (!startTime || !endTime) {
        setError('開始時間と終了時間を入力してください');
        return;
      }

      setLoading(true);
      setError(null);

      // 選択された日付を「yyyy-MM-dd」形式に変換
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      
      // シフトデータを作成
      const shiftData = {
        date: formattedDate,
        start_time: startTime,
        end_time: endTime,
        is_next_day: isNextDay
      };

      if (editingShift) {
        // 編集モードの場合は更新
        await apiService.put(`/api/shifts/${editingShift.id}`, shiftData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      } else {
        // 新規登録
        await apiService.post('/api/shifts', shiftData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }

      // 成功したらフォームをリセットして一覧を更新
      resetForm();
      fetchShifts();
    } catch (err) {
      
      setError('シフトの登録に失敗しました。再度お試しください。');
    } finally {
      setLoading(false);
      setOpenDialog(false);
    }
  };

  // シフト削除処理
  const handleDeleteShift = async (id) => {
    if (!window.confirm('このシフトを削除してもよろしいですか？')) {
      return;
    }

    try {
      setLoading(true);
      await apiService.delete(`/api/shifts/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      // 削除成功後、一覧を更新
      fetchShifts();
    } catch (err) {
      
      setError('シフトの削除に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  // シフト編集ダイアログを開く
  const handleOpenEditDialog = (shift) => {
    setEditingShift(shift);
    setSelectedDate(parseISO(shift.date));
    setStartTime(shift.start_time);
    setEndTime(shift.end_time);
    setIsNextDay(shift.is_next_day === true);
    setOpenDialog(true);
  };

  // 新規登録ダイアログを開く
  const handleOpenNewDialog = () => {
    resetForm();
    setOpenDialog(true);
  };

  // ダイアログを閉じる
  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  // フォームをリセット
  const resetForm = () => {
    setEditingShift(null);
    setSelectedDate(new Date());
    setStartTime('');
    setEndTime('');
    setIsNextDay(false);
  };

  // シフト一覧の表示
  const renderShifts = () => {
    if (shifts.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1">登録されているシフトはありません</Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        {shifts.map((shift) => (
          <Grid item xs={12} key={shift.id}>
            <Paper sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="subtitle1">
                  {format(parseISO(shift.date), 'yyyy年MM月dd日 (EEE)', { locale: ja })}
                </Typography>
                <Typography variant="body1">
                  {shift.start_time} - {shift.end_time}
                  {shift.is_next_day && ' (翌日)'}
                </Typography>
              </Box>
              <Box>
                <IconButton onClick={() => handleOpenEditDialog(shift)}>
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => handleDeleteShift(shift.id)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  };

  // 時間選択のためのオプション生成
  const timeOptions = Array.from({ length: 24 }, (_, i) => {
    const hour = i.toString().padStart(2, '0');
    return [
      `${hour}:00`,
      `${hour}:30`
    ];
  }).flat();

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        シフト管理
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Box sx={{ mb: 3 }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleOpenNewDialog}
          disabled={loading}
        >
          新しいシフトを登録
        </Button>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        renderShifts()
      )}
      
      {/* シフト登録/編集ダイアログ */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editingShift ? 'シフトを編集' : 'シフトを登録'}
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
              <DatePicker
                label="日付"
                value={selectedDate}
                onChange={(newDate) => setSelectedDate(newDate)}
                renderInput={(params) => <TextField {...params} fullWidth sx={{ mb: 2 }} />}
                inputFormat="yyyy年MM月dd日 (EEE)"
              />
            </LocalizationProvider>
            
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>開始時間</InputLabel>
                  <Select
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    label="開始時間"
                  >
                    {timeOptions.map((time) => (
                      <MenuItem key={`start-${time}`} value={time}>{time}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>終了時間</InputLabel>
                  <Select
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    label="終了時間"
                  >
                    {timeOptions.map((time) => (
                      <MenuItem key={`end-${time}`} value={time}>{time}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>翌日までのシフト</InputLabel>
              <Select
                value={isNextDay ? true : false}
                onChange={(e) => setIsNextDay(Boolean(e.target.value))}
                label="翌日までのシフト"
              >
                <MenuItem value={false}>当日内</MenuItem>
                <MenuItem value={true}>翌日にまたがる</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button 
            onClick={handleRegisterShift} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : (editingShift ? '更新' : '登録')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Shifts; 